import { useState, useEffect, useRef } from 'react';
import { Search, X, Loader2 } from 'lucide-react';
import { AvatarUploading } from '@/components/ui/avatar-uploading';
import { useSignUp } from '@/contexts/signup.context';
import { country } from '@sportspredict/shared';
import { AutocompleteDropdown } from '@/components/ui/autocomplete-dropdown';
import { useCollegeSearch, useClickOutside } from '@/hooks';

export const CollegeCountryForm = () => {
	const { country: selectedCountry, setCountry, college, setCollege } = useSignUp();

	const [countrySearch, setCountrySearch] = useState('');
	const [collegeSearch, setCollegeSearch] = useState('');

	const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState(false);
	const [isCollegeDropdownOpen, setIsCollegeDropdownOpen] = useState(false);

	const countryRef = useRef<HTMLDivElement>(null);
	const collegeRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (selectedCountry) {
			const found = country.find((c) => c.value === selectedCountry);
			setCountrySearch(found?.label ?? selectedCountry);
		}
		if (college) {
			setCollegeSearch(college);
		}
	}, [selectedCountry, college]);

	useClickOutside(countryRef, () => setIsCountryDropdownOpen(false));
	useClickOutside(collegeRef, () => setIsCollegeDropdownOpen(false));

	const filteredCountries = country.filter((c) =>
		c.label.toLowerCase().includes(countrySearch.toLowerCase())
	);

	const { results: filteredColleges, isLoading: isCollegeLoading } =
		useCollegeSearch(collegeSearch);

	const handleCountrySelect = (label: string) => {
		const found = country.find((c) => c.label === label);
		setCountry(found?.value);
		setCountrySearch(label);
		setIsCountryDropdownOpen(false);
	};

	const handleCollegeSelect = (name: string) => {
		setCollege(name);
		setCollegeSearch(name);
		setIsCollegeDropdownOpen(false);
	};

	const inputBase =
		'w-full px-4 h-[44px] rounded-full bg-[#1E2748] border border-[#506296] placeholder:text-[#B5B6BA] text-white focus:outline-hidden text-[16px] pr-10';

	return (
		<div className="w-full flex flex-col gap-8 text-white pb-6">
			<div className="flex flex-col gap-3 relative" ref={countryRef}>
				<div className="flex justify-between items-center w-full">
					<span className="text-white font-bold text-sm">Choose your country</span>
					<span className="text-[#B5B6BA] text-xs">Optional</span>
				</div>
				<div className="relative w-full">
					<input
						type="text"
						placeholder="Search country"
						className={inputBase}
						value={countrySearch}
						onChange={(e) => {
							setCountrySearch(e.target.value);
							setIsCountryDropdownOpen(true);
						}}
						onFocus={() => setIsCountryDropdownOpen(true)}
					/>
					{countrySearch ? (
						<button
							type="button"
							onClick={() => {
								setCountrySearch('');
							}}
							className="absolute right-3 top-1/2 -translate-y-1/2 text-[#B5B6BA] hover:text-white"
						>
							<X className="w-5 h-5" />
						</button>
					) : (
						<Search className="absolute right-3 top-1/2 -translate-y-1/2 text-[#B5B6BA] w-5 h-5 pointer-events-none" />
					)}
				</div>

				<AutocompleteDropdown
					isOpen={isCountryDropdownOpen}
					searchValue={countrySearch}
					items={filteredCountries}
					onSelect={handleCountrySelect}
				/>
			</div>

			<div
				className="flex flex-col gap-3 relative border-t-[0.5px] border-[#1C2D4A]/50 pt-6"
				ref={collegeRef}
			>
				<div className="flex justify-between items-center w-full">
					<span className="text-white font-bold text-sm">Add your College/University</span>
					<span className="text-[#B5B6BA] text-xs">Optional</span>
				</div>
				<div className="relative w-full">
					<input
						type="text"
						placeholder="Find your College or University"
						className={inputBase}
						value={collegeSearch}
						onChange={(e) => {
							setCollegeSearch(e.target.value);
							setCollege(e.target.value);
							setIsCollegeDropdownOpen(true);
						}}
						onFocus={() => setIsCollegeDropdownOpen(true)}
					/>
					{isCollegeLoading ? (
						<Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 text-[#B5B6BA] w-5 h-5 animate-spin pointer-events-none" />
					) : collegeSearch ? (
						<button
							type="button"
							onClick={() => {
								setCollegeSearch('');
								setCollege('');
							}}
							className="absolute right-3 top-1/2 -translate-y-1/2 text-[#B5B6BA] hover:text-white"
						>
							<X className="w-5 h-5" />
						</button>
					) : (
						<Search className="absolute right-3 top-1/2 -translate-y-1/2 text-[#B5B6BA] w-5 h-5 pointer-events-none" />
					)}
				</div>

				<AutocompleteDropdown
					isOpen={isCollegeDropdownOpen}
					searchValue={collegeSearch}
					items={filteredColleges}
					onSelect={handleCollegeSelect}
				/>
			</div>

			<div className="flex flex-col gap-3 border-t-[0.5px] border-[#1C2D4A]/50 pt-6">
				<div className="flex justify-between items-center w-full">
					<span className="text-white font-bold text-sm">Add a profile picture</span>
					<span className="text-[#B5B6BA] text-xs">Optional</span>
				</div>
				<div className="flex justify-center py-2">
					<AvatarUploading />
				</div>
			</div>
		</div>
	);
};
